import {
  Box,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  InputBase,
  Chip,
  Fade,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import { useTheme, alpha } from "@mui/material/styles";

export default function TicketsSidebar({
  search,
  setSearch,
  filteredTickets,
  selectedChat,
  openChat,
  chatStore,
  unread,
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: 280,
        minWidth: 280,
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid #F0F0F0",
        bgcolor: "#fff",
        "@keyframes fadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        animation: "fadeIn 0.4s ease forwards",
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 18,
            color: "#111827",
            letterSpacing: "-0.02em",
          }}
        >
          Support Hub
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            color: "#9CA3AF",
            mt: 0.25,
          }}
        >
          Manage active tickets
        </Typography>
      </Box>

      {/* Search */}
      <Box
        sx={{
          mx: 2,
          mb: 2,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          bgcolor: "#F9FAFB",
          border: "1px solid #F0F0F0",
          display: "flex",
          alignItems: "center",
          gap: 1,
          transition: "all 0.2s ease",
          "&:focus-within": {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.07)}`,
          },
        }}
      >
        <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />

        <InputBase
          placeholder="Search tickets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ fontSize: 13, flex: 1, color: "#374151" }}
        />
      </Box>

      {/* Conversation List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.08)",
            borderRadius: 2,
          },
        }}
      >
        {filteredTickets.length === 0 && search.trim() && (
          <Fade in timeout={300}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                px: 3,
              }}
            >
              <SearchIcon
                sx={{
                  fontSize: 36,
                  color: "#E5E7EB",
                  mb: 1.5,
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#6B7280",
                  mb: 0.5,
                }}
              >
                No results found
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#9CA3AF",
                  textAlign: "center",
                  lineHeight: 1.5,
                }}
              >
                No tickets match "{search}"
              </Typography>
            </Box>
          </Fade>
        )}

        <List disablePadding>
          {filteredTickets.map((t, idx) => {
            const chatId = String(t.id);
            const messages = chatStore?.[chatId] || [];
            const isSelected = selectedChat?.id === t.id;

            let lastPreview = t.subtitle || "No messages yet";
            let lastTime = "";

            if (messages.length > 0) {
              const last = messages[messages.length - 1];
              lastPreview =
                last.type === "image" ? "📷 Image" : last.text;
              lastTime = last.time || "";
            }

            return (
              <Fade in timeout={300 + idx * 60} key={t.id}>
                <ListItemButton
                  onClick={() => openChat(t)}
                  selected={isSelected}
                  sx={{
                    py: 1.5,
                    px: 2,
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 3,
                    border: isSelected
                      ? `2px solid ${theme.palette.primary.main}`
                      : "2px solid transparent",
                    bgcolor: isSelected
                      ? alpha(theme.palette.primary.main, 0.06)
                      : "transparent",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: isSelected
                        ? alpha(theme.palette.primary.main, 0.08)
                        : "#FAFBFC",
                    },
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: 3,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        border: `2px solid ${theme.palette.primary.main}`,
                      },
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 48 }}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: t.color || theme.palette.primary.main,
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 15,
                        transition: "transform 0.2s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      {t.name[0]}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: "#1F2937",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 130,
                          }}
                        >
                          {t.name}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          {lastTime && (
                            <Typography
                              sx={{
                                fontSize: 10,
                                color: "#9CA3AF",
                                fontWeight: 500,
                              }}
                            >
                              {lastTime}
                            </Typography>
                          )}

                          {unread?.[chatId] > 0 && (
                            <Chip
                              label={unread[chatId]}
                              size="small"
                              sx={{
                                height: 18,
                                minWidth: 18,
                                fontSize: 10,
                                fontWeight: 700,
                                borderRadius: "9px",
                                bgcolor: theme.palette.primary.main,
                                color: "#fff",
                                "& .MuiChip-label": {
                                  px: 0.5,
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#9CA3AF",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          mt: 0.25,
                        }}
                      >
                        {lastPreview}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </Fade>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}
